'use strict';

define('vb/private/types/utils/serviceDataProviderRestHelper',[
  'knockout',
  'vb/private/helpers/rest',
  'vb/private/services/transformsUtils',
  'vb/private/types/dataProviderConstants',
  'vb/private/configLoader',
  'vb/private/constants',
  'vb/private/services/ramp/transformsUtils',
  'vb/private/services/ramp/rampTransforms',
  'vb/private/services/servicesLoader',
  'vb/private/services/serviceResolverFactory',
  'vb/private/services/operationRefEndpointProvider',
  'vb/private/services/serviceConstants',
  'vb/private/services/serviceUtils',
  'vb/private/utils',
], (ko, RestHelper, TransformsUtils, DPConstants, ConfigLoader, Constants, RampTransformsUtils, RampTransforms,
  ServicesLoader, ServiceResolverFactory, OperationRefEndpointProvider, ServiceConstants, ServiceUtils, Utils) => {
  /**
   * Stores Endpoint runtime state. Stores any state for an endpoint identified by its scope:endpointId and that can
   * be shared across multiple usages of this endpoint loaded in a given scope. Currently there are 2 properties
   * that can be stored here -
   * - capabilities : capabilities by feature as define by JET DataProvider.
   * - totalSize: canonical totalSize
   * @type {{}}
   */
  const ENDPOINT_RT_STATE = {};
  const TOTALSIZE_KEY = 'totalSize';
  /**
   * Helper specifically used with SDP so it can control restHelper behavior.
   *
   * @see {RestHelper}
   */
  class ServiceDataProviderRestHelper extends RestHelper {
    static storeInCache(name, key, value) {
      ENDPOINT_RT_STATE[name] = ENDPOINT_RT_STATE[name] || {};
      const keyValue = ENDPOINT_RT_STATE[name][key];
      if (ko.isObservable(keyValue)) {
        keyValue(value);
      } else {
        ENDPOINT_RT_STATE[name][key] = value;
      }
    }

    static retrieveFromCache(name, key) {
      return (ENDPOINT_RT_STATE[name] && ENDPOINT_RT_STATE[name][key]);
    }

    /**
     * depending on capabilities not all transforms need to be run
     * @returns {*}
     */
    getRequestTransformsToRun(configuration) {
      if (configuration.fetchConfiguration) {
        const fetchCall = configuration.fetchConfiguration.capability;
        const fetchCallParams = configuration.fetchConfiguration.fetchParameters;
        if (fetchCall === 'fetchByKeys' && fetchCallParams.keys.size > 0
          && this.transformRequestFuncMap.fetchByKeys
        ) {
          // transforms author defines a fetchByKeys function use it
          const retXform = ['fetchByKeys'];

          const ctx = configuration.fetchConfiguration.context;
          if (ctx) {
            if (ctx.capabilities && ctx.capabilities.fetchByKeys
              && ctx.capabilities.fetchByKeys.implementation === 'iteration'
              && ctx.transformsContext && ctx.transformsContext['temp-for-2404-vb-includePaginateXForm'] === true
            ) {
              // optIn to use paginate transform so that the row of key beyond first batch can be returned
              // this is an internal temporary flag to unblock fetchByKeys encounter infinite loop
              retXform.push('paginate');
            }

            if (ctx.transformsContext && ctx.transformsContext['vb-includeSelectXForm'] === true
            ) {
              // optIn to use select transform so that all fetch methods are in sync
              retXform.push('select');
            }
          }
          return retXform;
        }
      }
      const transformFuncKeys = Object.keys(this.transformRequestFuncMap);
      // call select transform first and body last
      return transformFuncKeys
        .filter(TransformsUtils.excludeFetchByKeys)
        .sort(TransformsUtils.selectTransformFirst)
        .sort(TransformsUtils.bodyTransformLast); // to allow other transforms to update body
    }

    /**
     * Only for SDPs we may need to resolve the external references found in the response metadata.
     * Checks transforms requirements method to see if it requires fully resolved endpoint metadata.
     *
     * BOSS Transforms needs this resolved metadata to calculate endpoint capabilities.
     *
     * @param {Object} transformsMetadataFuncs Value returned by Endpoint.getMetadataTransforms()
     * @param {Object} configuration
     * @param {string} configuration.url
     * @param {Object} configuration.parameters
     * @param {Object} configuration.endpointDefinition Endpoint metadata from the OpenApi doc
     * @param {Object} configuration.initConfig

     * @returns
     */
    // eslint-disable-next-line class-methods-use-this
    transformsUseResponsesMetadata(transformsMetadataFuncs, configuration) {
      return TransformsUtils.transformsUseResponsesMetadata(transformsMetadataFuncs, configuration);
    }

    // eslint-disable-next-line class-methods-use-this
    storeState(name, key, value) {
      ServiceDataProviderRestHelper.storeInCache(name, key, value);
    }

    // eslint-disable-next-line class-methods-use-this
    retrieveState(name, key) {
      return ServiceDataProviderRestHelper.retrieveFromCache(name, key);
    }

    /**
     * Stores the endpoint capabilities and initializes the companion runtime state used by SDP fetches.
     * @param {string} name cache key for the endpoint
     * @param {Object} caps capabilities to cache
     * @returns {Object} the stored capabilities object
     * @private
     */
    static storeCapabilities(name, caps) {
      ServiceDataProviderRestHelper.storeInCache(name, DPConstants.CAPABILITIES_KEY, caps);
      const totalSize = ko.observable(-1);
      ServiceDataProviderRestHelper.storeInCache(name, TOTALSIZE_KEY, totalSize);
      return caps;
    }

    /**
     * Adds a service match to a list unless that service name/namespace pair is already present.
     * @param {Object[]} candidates list of catalog service candidates to update
     * @param {Object|string|null} candidate service match object or service name
     * @private
     */
    static addCatalogServiceCandidate(candidates, candidate) {
      if (!candidate) {
        return;
      }

      const serviceName = typeof candidate === 'string'
        ? candidate
        : (candidate.serviceName || candidate.serviceId);
      const namespace = typeof candidate === 'string' ? undefined : candidate.namespace;
      const key = `${namespace || ''}:${serviceName}`;
      if (serviceName && !candidates.some((service) => service.key === key)) {
        candidates.push({ serviceName, namespace, key });
      }
    }

    /**
     * Loads transforms for one catalog service candidate.
     * @param {Object} candidate service name/namespace pair
     * @param {string} containerNamespace namespace requesting access to the service
     * @returns {Promise<Object|null>} transforms metadata, or null if the candidate has none or cannot be loaded
     * @private
     */
    getCatalogTransformsForService(candidate, containerNamespace) {
      const { serviceName, namespace } = candidate;
      const qualifiedServiceName = namespace ? `${namespace}:${serviceName}` : serviceName;
      const catalogUrl = `${Constants.VbProtocols.CATALOG}://${ServiceConstants.ExtensionTypes.SERVICES}`
        + `/${qualifiedServiceName}`;

      return ServicesLoader.getCatalogExtensions(ConfigLoader.protocolRegistry,
        catalogUrl,
        serviceName,
        containerNamespace,
        undefined,
        undefined,
        true, // skipTrapExtensions
        true) // useUrlMapping
        .then((catalogInfo) => catalogInfo
          && catalogInfo.mergedExtensions
          && catalogInfo.mergedExtensions.transforms)
        .catch((error) => {
          this.log.fine('Unable to get transforms from catalog service', qualifiedServiceName,
            'for ServiceDataProviderRestHelper', this.id, error);
          return null;
        });
    }

    /**
     * Resolves catalog transforms for operationRef endpoints from the current namespace context.
     * @returns {Promise<Object|null>} transforms metadata for the matched catalog service, or null if none is found
     * @private
     */
    findTransformsInCatalog() {
      const endpointReference = (this._endpointId && this._endpointId.url && this._endpointId.operationRef)
        ? this.endpointReference
        : this._endpointId;
      if (!endpointReference || typeof endpointReference !== 'object') {
        return Promise.resolve(null);
      }

      const { url, operationRef, containerNamespace } = endpointReference;
      if (!url || !operationRef) {
        return Promise.resolve(null);
      }

      const serviceCandidates = [];
      ServiceDataProviderRestHelper.addCatalogServiceCandidate(serviceCandidates, endpointReference);

      const refNamespace = ServiceResolverFactory.getNamespace(this._serviceContext);
      return OperationRefEndpointProvider.findMatchInMetadata(url, refNamespace)
        .catch((error) => {
          this.log.fine('Unable to match endpoint URL to catalog metadata for ServiceDataProviderRestHelper',
            this.id, error);
          return null;
        })
        .then((metadataMatch) => {
          ServiceDataProviderRestHelper.addCatalogServiceCandidate(serviceCandidates, metadataMatch);
          return OperationRefEndpointProvider.findMatchInCatalog(operationRef, refNamespace)
            .catch((error) => {
              this.log.fine('Unable to match operationRef to catalog service for ServiceDataProviderRestHelper',
                this.id, error);
              return null;
            });
        })
        .then((catalogMatch) => {
          ServiceDataProviderRestHelper.addCatalogServiceCandidate(serviceCandidates, catalogMatch);
          return serviceCandidates.reduce((transformsPromise, candidate) => transformsPromise.then(
            (transforms) => transforms || this.getCatalogTransformsForService(candidate, containerNamespace),
          ), Promise.resolve(null));
        });
    }

    /**
     * Resolves catalog transform paths so non-alias modules can be loaded for metadata capability checks.
     * This intentionally mirrors only the catalog-origin path logic used by service definitions.
     * @param {Object|string} catalogTransforms catalog transforms declaration
     * @returns {string|null} resolved transforms path, or null when it cannot be safely resolved
     * @private
     */
    static resolveCatalogTransformsPath(catalogTransforms = {}) {
      const transformsDef = catalogTransforms || {};
      let path = typeof transformsDef === 'string' ? transformsDef : transformsDef.path;
      const txOrigin = transformsDef.origin;

      if (!path || (path.indexOf(Constants.PARENT_FOLDER) >= 0 && !txOrigin)) {
        return null;
      }

      if (path.startsWith(Constants.RELATIVE_FOLDER_PREFIX)) {
        const servicePath = txOrigin && txOrigin.path;
        if (servicePath) {
          const index = servicePath.lastIndexOf(Constants.PATH_SEPARATOR);
          const prefix = index <= 0 ? '' : servicePath.substring(0, index + 1);
          path = `${prefix}${path}`;
        }
      }

      return path;
    }

    /**
     * Loads a catalog transform module without running the full service-definition transform merge flow.
     * @param {Object|string} catalogTransforms catalog transforms declaration
     * @returns {Promise<Object|null>} loaded transform module, or null when there is no loadable path
     * @private
     */
    // eslint-disable-next-line class-methods-use-this
    loadCatalogTransformModule(catalogTransforms) {
      const path = ServiceDataProviderRestHelper.resolveCatalogTransformsPath(catalogTransforms);
      const uri = ServiceUtils.getModuleUri(path);
      if (uri) {
        return Utils.getRuntimeEnvironment()
          .then((rtEnv) => rtEnv.getTransformsSource(uri.toString()));
      }

      return Promise.resolve(null);
    }

    /**
     * Checks whether a loaded transform module reuses the built-in RAMP metadata implementation.
     * @param {Object} transformModule loaded transform module
     * @returns {boolean}
     * @private
     */
    static usesRampMetadataTransforms(transformModule) {
      const metadata = transformModule && transformModule.metadata;
      const rampMetadata = RampTransforms.metadata;
      const rampCapabilities = rampMetadata && rampMetadata.capabilities;

      return !!metadata && (metadata === rampMetadata || metadata.capabilities === rampCapabilities);
    }

    /**
     * Returns cached default capabilities for known catalog transforms when endpoint metadata is not needed.
     * @param {string} name cache key for the endpoint
     * @returns {Promise<Object|null>} default capabilities or null when endpoint metadata should be used
     * @private
     */
    getDefaultCapabilitiesFromCatalog(name) {
      return this.findTransformsInCatalog()
        .catch((error) => {
          this.log.warn('Error getting transforms from catalog for ServiceDataProviderRestHelper', this.id, error);
          return null;
        })
        .then((catalogTransforms) => {
          if (!catalogTransforms) {
            return null;
          }

          const catalogTransformsPath = ServiceDataProviderRestHelper.resolveCatalogTransformsPath(catalogTransforms);
          if (RampTransformsUtils.isBusinessObjectsTransformsPath(catalogTransformsPath)) {
            this.log.fine('Using standard business object capabilities for', name,
              'for ServiceDataProviderRestHelper', this.id);
            return RampTransformsUtils.getBusinessObjectsCapabilities();
          }

          return this.loadCatalogTransformModule(catalogTransforms)
            .then((transformModule) => {
              if (ServiceDataProviderRestHelper.usesRampMetadataTransforms(transformModule)) {
                this.log.fine('Using standard business object capabilities from catalog transform metadata for', name,
                  'for ServiceDataProviderRestHelper', this.id);
                return RampTransformsUtils.getBusinessObjectsCapabilities();
              }
              return null;
            })
            .catch((error) => {
              this.log.fine('Unable to load catalog transforms for default capabilities', name,
                'for ServiceDataProviderRestHelper', this.id, error);
              return null;
            });
        });
    }

    /**
     * Derives capabilities from the endpoint metadata transforms.
     * @returns {Promise<Object>} resolves with the capabilities or an empty object
     * @private
     */
    getCapabilitiesFromEndpoint() {
      let caps = {};
      let capabilitiesFunc;
      return this._getEndpoint().then((endpoint) => {
        if (endpoint) {
          // endpoint.getConfig() loads the endpoint and any existing transforms
          return endpoint.getConfig(this._allParams, { ignoreMissingParams: true })
            .then((config) => {
              const transformsMetadataFuncs = endpoint.getMetadataTransforms();
              capabilitiesFunc = transformsMetadataFuncs && transformsMetadataFuncs.capabilities;

              return Promise.all([
                config,
                transformsMetadataFuncs,
                capabilitiesFunc && endpoint.getMetadata(false), // fetch metadata only if we need it
              ]);
            })
            .then(([config, transformsMetadataFuncs, endpointMetadata]) => {
              // initial endpoint metadata does not have external references resolved
              let epMetadata = endpointMetadata;
              const configuration = {
                url: config.url,
                parameters: this._flattenedParams(), // this is always a new object
                endpointDefinition: epMetadata,
                initConfig: {},
              };

              // if transform needs external refs resolved it will need to indicate that
              if (capabilitiesFunc && this.transformsUseResponsesMetadata(transformsMetadataFuncs, configuration)) {
                // expand the responses metadata
                epMetadata = endpoint.getMetadata(true);
              }
              return Promise.all([
                configuration,
                epMetadata,
              ]);
            })
            .then(([configuration, endpointMetadata]) => {
              if (capabilitiesFunc) {
                // update configuration with the (potentially) expanded references found in the endpoint definition
                // eslint-disable-next-line no-param-reassign
                configuration.endpointDefinition = endpointMetadata;
                Object.assign(configuration.initConfig, this.initRequestMap);

                caps = capabilitiesFunc.call(null, configuration);
              }
              return caps;
            });
        }
        this.log.error('ServiceDataProviderRestHelper', this.id, 'error fetching endpoint details',
          JSON.stringify(this.endpointReference));
        return caps;
      }).catch((e) => {
        this.log.error('ServiceDataProviderRestHelper', this.id, 'error fetching full URL for endpoint',
          JSON.stringify(this.endpointReference), e);
        return caps;
      });
    }

    /**
     * Gets and stores the capabilities for the endpoint. This method should be called once per scope#endpoint
     * @param name
     * @return {Promise<*>} resolves with the capabilities or undefined
     */
    getAndStoreCapabilities(name) {
      let capsPromise = this.retrieveState(name, DPConstants.CAPABILITIES_KEY);
      if (!capsPromise) {
        capsPromise = this.getDefaultCapabilitiesFromCatalog(name)
          .then((defaultCapabilities) => defaultCapabilities || this.getCapabilitiesFromEndpoint())
          .then((caps) => ServiceDataProviderRestHelper.storeCapabilities(name, caps))
          .catch((e) => {
            this.log.warn('ServiceDataProviderRestHelper', this.id, 'error getting capabilities for endpoint',
              JSON.stringify(this.endpointReference), e);
            return ServiceDataProviderRestHelper.storeCapabilities(name, {});
          });
        ServiceDataProviderRestHelper.storeInCache(name, DPConstants.CAPABILITIES_KEY, capsPromise);
      }
      return capsPromise;
    }
  }

  return ServiceDataProviderRestHelper;
});

